// src/pages/Billing/PaymentSuccess.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { CheckCircle, ArrowLeft, Home } from 'lucide-react';
import config from '../../Config';
import * as Sentry from "@sentry/react";

const PaymentSuccess = () => {
    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState(null);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkPaymentStatus = async () => {
            try {
                const queryParams = new URLSearchParams(location.search);
                const sessionId = queryParams.get('session_id');

                if (!sessionId) {
                    setError("No session ID provided. Cannot verify payment status.");
                    setLoading(false);
                    return;
                }

                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                if (!userData?.token) {
                    navigate('/login', { replace: true });
                    return;
                }

                const response = await fetch(`${config.apiBaseUrl}/payment/session/${sessionId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'user-authorization': userData.token
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setPayment(data);
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to verify payment status');
                }
            } catch (error) {
                Sentry.captureException(error);
                setError(error.message || 'An unexpected error occurred while verifying your payment');
                console.error('Payment verification error:', error);
            } finally {
                setLoading(false);
            }
        };

        // Start verification process
        checkPaymentStatus();
    }, [location.search, navigate]);

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const renderLoadingState = () => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
                Verifying your payment...
            </Typography>
            <Typography variant="body1" color="text.secondary">
                This may take a few moments. Please don't close this page.
            </Typography>
        </Box>
    );

    const renderErrorState = () => (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Alert
                severity="error"
                variant="outlined"
                sx={{
                    maxWidth: 500,
                    mx: 'auto',
                    mb: 4,
                    px: 3,
                    py: 2
                }}
            >
                {error}
            </Alert>

            <Box sx={{ mt: 4 }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate('/billing')}
                    sx={{ mr: 2 }}
                >
                    Back to Billing
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<Home size={18} />}
                    onClick={() => navigate('/')}
                >
                    Go to Dashboard
                </Button>
            </Box>
        </Box>
    );

    const renderSuccessState = () => (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'success.light', borderRadius: '50%', mb: 3 }}>
                <CheckCircle size={48} color="#2e7d32" />
            </Box>

            <Typography variant="h4" gutterBottom>
                Payment Successful!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
                                        Your payment has been processed successfully and your account has been credited with the purchased API credits.
            </Typography>

            {payment && (
                <Card variant="outlined" sx={{ maxWidth: 500, mx: 'auto', mb: 4, textAlign: 'left' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Payment Details
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Amount:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {formatCurrency(payment.amount, payment.currency)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">API Credits:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {payment.points.toLocaleString()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Date:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {new Date(payment.created_at).toLocaleDateString()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Status:</Typography>
                            <Typography
                                variant="body1"
                                fontWeight="500"
                                sx={{
                                    color: payment.status === 'completed' ? 'success.main' : 'warning.main'
                                }}
                            >
                                {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Home size={18} />}
                    onClick={() => navigate('/')}
                    sx={{ mr: 2 }}
                >
                    Go to Dashboard
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate('/billing')}
                >
                    Back to Billing
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            bgcolor: '#f8f9fa',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3
        }}>
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: 700,
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 3, px: 4 }}>
                    <Typography variant="h5" fontWeight="500">
                        Payment Result
                    </Typography>
                </Box>

                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    {loading ? renderLoadingState() :
                     error ? renderErrorState() :
                     renderSuccessState()}
                </Box>
            </Paper>
        </Box>
    );
};

export default PaymentSuccess;